import { useEffect, useState, useCallback } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { formatDate, formatWeekRange, formatTimestamp, formatMonth } from '../utils/formatters'
import './Statistics.css'

// Water types
interface DailyWaterUsage {
  date: string
  base_usage: number
  shower_usage: number
  total_usage: number
  shower_count: number
}

interface WeeklyWaterUsage {
  week_start: string
  week_end: string
  base_usage: number
  shower_usage: number
  total_usage: number
  shower_count: number
  day_count: number
}

interface ShowerStats {
  total_showers: number
  last_7_days: number
  last_30_days: number
  avg_per_week: number
  avg_liters_per_week: number
}

interface RefillEvent {
  id: number
  timestamp: string
  amount_liters: number | null
  is_full_tank: boolean
  cost?: number
  notes?: string
  location?: string
}

interface WaterSettings {
  tank_capacity: number
  daily_usage: number
  shower_usage: number
}

interface WaterStatistics {
  daily_usage: DailyWaterUsage[]
  weekly_usage: WeeklyWaterUsage[]
  shower_frequency: ShowerStats
  refill_history: RefillEvent[]
  settings: WaterSettings
}

// Fuel types
interface FuelEconomyData {
  date: string
  dashboard_km_per_l: number
  calculated_km_per_l: number
  odometer_start: number
  odometer_end: number
}

interface HeaterUsageData {
  date: string
  heater_usage_liters: number
  driving_fuel_liters: number
  total_fuel_liters: number
  days_in_cycle: number
}

interface MonthlyCost {
  month: string
  total_cost: number
  total_liters: number
  refuel_count: number
}

interface FuelSummary {
  total_refuels: number
  total_fuel_liters: number
  total_cost: number
  avg_dashboard_km_per_l: number
  avg_calculated_km_per_l: number
  avg_heater_usage_l_per_day: number
  total_heater_usage: number
  total_driving_fuel: number
}

interface FuelStatistics {
  fuel_economy: FuelEconomyData[]
  heater_usage: HeaterUsageData[]
  monthly_fuel_costs: MonthlyCost[]
  summary: FuelSummary
}

// Battery types
interface DailyBatteryData {
  date: string
  avg_soc: number
  min_soc: number
  max_soc: number
  start_soc: number
  end_soc: number
  readings: number
}

interface SOCDataPoint {
  timestamp: string
  soc: number
  voltage: number
}

interface BatterySummary {
  current_soc: number
  current_voltage: number
  avg_daily_consumption: number
  avg_soc: number
  min_soc: number
  max_soc: number
  total_readings: number
  days_with_data: number
}

interface BatteryStatistics {
  daily_pattern: DailyBatteryData[]
  soc_over_time: SOCDataPoint[]
  summary: BatterySummary
}

// Expenses types
interface CategoryExpense {
  category: string
  total_native: number
  count: number
}

interface MonthlyExpense {
  month: string
  total_native: number
  count: number
  by_category: Record<string, number>
}

interface ExpensesSummary {
  total_expenses: number
  total_spend_native: number
  avg_per_month: number
  top_category: string
  top_category_spend: number
}

interface ExpensesStatistics {
  by_category: CategoryExpense[]
  monthly_breakdown: MonthlyExpense[]
  summary: ExpensesSummary
  native_currency: string
}

// Propane types
interface PropaneReplacement {
  id: number
  timestamp: string
  tank_size_kg?: number
  cost?: number
  currency?: string
  notes?: string
  days_since_prev?: number
}

interface DaysBetweenData {
  date: string
  days_between: number
  replacement_num: number
}

interface PropaneSummary {
  total_replacements: number
  total_cost: number
  avg_days_per_tank: number
  min_days: number
  max_days: number
  annual_cost_estimate: number
  days_since_last_replace: number
}

interface PropaneStatistics {
  replacement_history: PropaneReplacement[]
  days_between: DaysBetweenData[]
  summary: PropaneSummary
  native_currency: string
}

// AdBlue types
interface AdBlueRefill {
  id: number
  timestamp: string
  amount_liters: number
  price?: number
  currency?: string
  notes?: string
  odometer?: number
  km_per_liter?: number
}

interface AdBlueConsumptionData {
  date: string
  km_per_liter: number
  distance_km: number
  amount_liters: number
}

interface AdblueSummary {
  total_refills: number
  total_liters: number
  total_cost: number
  avg_km_per_liter: number
  avg_liters_per_month: number
  annual_cost_estimate: number
}

interface AdBlueStatistics {
  refill_history: AdBlueRefill[]
  consumption_data: AdBlueConsumptionData[]
  summary: AdblueSummary
  native_currency: string
}

type ChartView = 'daily' | 'weekly'
type StatsSection = 'water' | 'fuel' | 'battery' | 'expenses' | 'propane' | 'adblue'

export function Statistics() {
  const { user } = useAuth()
  const [section, setSection] = useState<StatsSection>('water')
  const [waterData, setWaterData] = useState<WaterStatistics | null>(null)
  const [fuelData, setFuelData] = useState<FuelStatistics | null>(null)
  const [batteryData, setBatteryData] = useState<BatteryStatistics | null>(null)
  const [expensesData, setExpensesData] = useState<ExpensesStatistics | null>(null)
  const [propaneData, setPropaneData] = useState<PropaneStatistics | null>(null)
  const [adblueData, setAdblueData] = useState<AdBlueStatistics | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [chartView, setChartView] = useState<ChartView>('daily')

  const fetchWaterStats = useCallback(async () => {
    try {
      const response = await fetch('/api/v1/statistics/water')
      if (!response.ok) {
        throw new Error('Failed to fetch water statistics')
      }
      const statsData = await response.json()
      setWaterData(statsData)
      setError(null)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error')
    } finally {
      setLoading(false)
    }
  }, [])

  const fetchFuelStats = useCallback(async () => {
    try {
      const response = await fetch('/api/v1/statistics/fuel')
      if (!response.ok) {
        throw new Error('Failed to fetch fuel statistics')
      }
      const statsData = await response.json()
      setFuelData(statsData)
      setError(null)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error')
    } finally {
      setLoading(false)
    }
  }, [])

  const fetchBatteryStats = useCallback(async () => {
    try {
      const response = await fetch('/api/v1/statistics/battery')
      if (!response.ok) {
        throw new Error('Failed to fetch battery statistics')
      }
      const statsData = await response.json()
      setBatteryData(statsData)
      setError(null)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error')
    } finally {
      setLoading(false)
    }
  }, [])

  const fetchExpensesStats = useCallback(async () => {
    try {
      const response = await fetch('/api/v1/statistics/expenses')
      if (!response.ok) {
        throw new Error('Failed to fetch expenses statistics')
      }
      const statsData = await response.json()
      setExpensesData(statsData)
      setError(null)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error')
    } finally {
      setLoading(false)
    }
  }, [])

  const fetchPropaneStats = useCallback(async () => {
    try {
      const response = await fetch('/api/v1/statistics/propane')
      if (!response.ok) {
        throw new Error('Failed to fetch propane statistics')
      }
      const statsData = await response.json()
      setPropaneData(statsData)
      setError(null)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error')
    } finally {
      setLoading(false)
    }
  }, [])

  const fetchAdblueStats = useCallback(async () => {
    try {
      const response = await fetch('/api/v1/statistics/adblue')
      if (!response.ok) {
        throw new Error('Failed to fetch AdBlue statistics')
      }
      const statsData = await response.json()
      setAdblueData(statsData)
      setError(null)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    if (!user) return
    setLoading(true)
    if (section === 'water') {
      fetchWaterStats()
    } else if (section === 'fuel') {
      fetchFuelStats()
    } else if (section === 'battery') {
      fetchBatteryStats()
    } else if (section === 'expenses') {
      fetchExpensesStats()
    } else if (section === 'propane') {
      fetchPropaneStats()
    } else {
      fetchAdblueStats()
    }
  }, [user, section, fetchWaterStats, fetchFuelStats, fetchBatteryStats, fetchExpensesStats, fetchPropaneStats, fetchAdblueStats])

  // Get max value for chart scaling
  const getMaxUsage = (daily: DailyWaterUsage[], weekly: WeeklyWaterUsage[]): number => {
    const dailyMax = Math.max(...daily.map(d => d.total_usage), 1)
    const weeklyMax = Math.max(...weekly.map(w => w.total_usage), 1)
    return chartView === 'daily' ? dailyMax : weeklyMax
  }

  // Get max value for heater/driving fuel chart
  const getMaxFuel = (): number => {
    if (!fuelData?.heater_usage.length) return 1
    return Math.max(...fuelData.heater_usage.map(h => h.total_fuel_liters), 1)
  }

  // Get max value for monthly costs chart
  const getMaxCost = (): number => {
    if (!fuelData?.monthly_fuel_costs.length) return 1
    return Math.max(...fuelData.monthly_fuel_costs.map(m => m.total_cost), 1)
  }

  if (loading) {
    return (
      <div className="page">
        <h1>Statistics</h1>
        <p>Loading...</p>
      </div>
    )
  }

  if (error) {
    return (
      <div className="page">
        <h1>Statistics</h1>
        <p className="text-danger">Error: {error}</p>
      </div>
    )
  }

  const maxUsage = waterData ? getMaxUsage(waterData.daily_usage, waterData.weekly_usage) : 1
  const maxFuel = getMaxFuel()
  const maxCost = getMaxCost()

  return (
    <div className="page statistics">
      <h1>Statistics</h1>

      {/* Section Toggle */}
      <div className="section-toggle">
        <button
          className={`section-btn ${section === 'water' ? 'section-btn--active' : ''}`}
          onClick={() => setSection('water')}
        >
          Water
        </button>
        <button
          className={`section-btn ${section === 'fuel' ? 'section-btn--active' : ''}`}
          onClick={() => setSection('fuel')}
        >
          Fuel
        </button>
        <button
          className={`section-btn ${section === 'battery' ? 'section-btn--active' : ''}`}
          onClick={() => setSection('battery')}
        >
          Battery
        </button>
        <button
          className={`section-btn ${section === 'expenses' ? 'section-btn--active' : ''}`}
          onClick={() => setSection('expenses')}
        >
          Expenses
        </button>
        <button
          className={`section-btn ${section === 'propane' ? 'section-btn--active' : ''}`}
          onClick={() => setSection('propane')}
        >
          Propane
        </button>
        <button
          className={`section-btn ${section === 'adblue' ? 'section-btn--active' : ''}`}
          onClick={() => setSection('adblue')}
        >
          AdBlue
        </button>
      </div>

      {/* Water Section */}
      {section === 'water' && (
        <>
          {/* Water Usage Chart Section */}
          <div className="card stats-card">
            <div className="card-header">
              <h3 className="card-title">Water Usage</h3>
              <div className="chart-view-toggle">
                <button
                  className={`toggle-btn ${chartView === 'daily' ? 'toggle-btn--active' : ''}`}
                  onClick={() => setChartView('daily')}
                >
                  Daily
                </button>
                <button
                  className={`toggle-btn ${chartView === 'weekly' ? 'toggle-btn--active' : ''}`}
                  onClick={() => setChartView('weekly')}
                >
                  Weekly
                </button>
              </div>
            </div>

            <div className="chart-container">
              {chartView === 'daily' ? (
                <div className="bar-chart">
                  {waterData?.daily_usage.map((day, index) => (
                    <div key={index} className="bar-wrapper">
                      <div className="bar-container" title={`${day.total_usage.toFixed(1)}L total`}>
                        <div
                          className="bar bar--base"
                          style={{ height: `${(day.base_usage / maxUsage) * 100}%` }}
                        />
                        <div
                          className="bar bar--shower"
                          style={{ height: `${(day.shower_usage / maxUsage) * 100}%` }}
                        />
                      </div>
                      <div className="bar-label">{formatDate(day.date)}</div>
                      {day.shower_count > 0 && (
                        <div className="bar-shower-indicator">{day.shower_count}</div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="bar-chart">
                  {waterData?.weekly_usage.map((week, index) => (
                    <div key={index} className="bar-wrapper">
                      <div className="bar-container" title={`${week.total_usage.toFixed(1)}L total`}>
                        <div
                          className="bar bar--base"
                          style={{ height: `${(week.base_usage / maxUsage) * 100}%` }}
                        />
                        <div
                          className="bar bar--shower"
                          style={{ height: `${(week.shower_usage / maxUsage) * 100}%` }}
                        />
                      </div>
                      <div className="bar-label bar-label--week">{formatWeekRange(week.week_start, week.week_end)}</div>
                      {week.shower_count > 0 && (
                        <div className="bar-shower-indicator">{week.shower_count}</div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="chart-legend">
              <div className="legend-item">
                <span className="legend-color legend-color--base"></span>
                <span className="legend-label">Base usage ({waterData?.settings.daily_usage}L/day)</span>
              </div>
              <div className="legend-item">
                <span className="legend-color legend-color--shower"></span>
                <span className="legend-label">Showers ({waterData?.settings.shower_usage}L each)</span>
              </div>
            </div>
          </div>

          {/* Shower Frequency Section */}
          <div className="card stats-card">
            <div className="card-header">
              <h3 className="card-title">Shower Frequency</h3>
            </div>

            <div className="shower-stats">
              <div className="stat-item">
                <span className="stat-value">{waterData?.shower_frequency.last_7_days ?? 0}</span>
                <span className="stat-label">Last 7 days</span>
              </div>
              <div className="stat-item">
                <span className="stat-value">{waterData?.shower_frequency.last_30_days ?? 0}</span>
                <span className="stat-label">Last 30 days</span>
              </div>
              <div className="stat-item">
                <span className="stat-value">{waterData?.shower_frequency.avg_per_week.toFixed(1) ?? '0.0'}</span>
                <span className="stat-label">Avg/week</span>
              </div>
              <div className="stat-item">
                <span className="stat-value">{waterData?.shower_frequency.avg_liters_per_week.toFixed(0) ?? '0'}</span>
                <span className="stat-label">L/week</span>
              </div>
            </div>

            <div className="shower-total">
              <span className="shower-total-label">Total showers recorded:</span>
              <span className="shower-total-value">{waterData?.shower_frequency.total_showers ?? 0}</span>
            </div>
          </div>

          {/* Refill History Section */}
          <div className="card stats-card">
            <div className="card-header">
              <h3 className="card-title">Refill History</h3>
              <span className="badge badge--muted">{waterData?.refill_history.length ?? 0} refills</span>
            </div>

            {waterData?.refill_history && waterData.refill_history.length > 0 ? (
              <div className="refill-list">
                {waterData.refill_history.map((refill) => (
                  <div key={refill.id} className="refill-item">
                    <div className="refill-info">
                      <div className="refill-date">{formatTimestamp(refill.timestamp)}</div>
                      {refill.location && (
                        <div className="refill-location">{refill.location}</div>
                      )}
                      {refill.notes && (
                        <div className="refill-notes">{refill.notes}</div>
                      )}
                    </div>
                    <div className="refill-details">
                      <div className="refill-amount">
                        {refill.is_full_tank ? (
                          <span className="badge badge--success">Full Tank</span>
                        ) : refill.amount_liters ? (
                          <span>{refill.amount_liters.toFixed(0)}L</span>
                        ) : (
                          <span className="text-muted">--</span>
                        )}
                      </div>
                      {refill.cost !== undefined && refill.cost > 0 && (
                        <div className="refill-cost">{refill.cost.toFixed(2)}</div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="no-data">
                <p className="text-muted">No refill history available</p>
              </div>
            )}
          </div>
        </>
      )}

      {/* Fuel Section */}
      {section === 'fuel' && (
        <>
          {/* Fuel Economy Chart */}
          <div className="card stats-card">
            <div className="card-header">
              <h3 className="card-title">Fuel Economy</h3>
              <span className="badge badge--muted">km/L over time</span>
            </div>

            {fuelData?.fuel_economy && fuelData.fuel_economy.length > 0 ? (
              <>
                <div className="chart-container">
                  <div className="line-chart">
                    {fuelData.fuel_economy.map((entry, index) => {
                      const maxKmL = Math.max(
                        ...fuelData.fuel_economy.map(e => Math.max(e.dashboard_km_per_l, e.calculated_km_per_l)),
                        1
                      )
                      return (
                        <div key={index} className="line-point-wrapper">
                          <div className="line-points">
                            <div
                              className="line-point line-point--dashboard"
                              style={{ bottom: `${(entry.dashboard_km_per_l / maxKmL) * 100}%` }}
                              title={`Dashboard: ${entry.dashboard_km_per_l.toFixed(1)} km/L`}
                            />
                            {entry.calculated_km_per_l > 0 && (
                              <div
                                className="line-point line-point--calculated"
                                style={{ bottom: `${(entry.calculated_km_per_l / maxKmL) * 100}%` }}
                                title={`Calculated: ${entry.calculated_km_per_l.toFixed(1)} km/L`}
                              />
                            )}
                          </div>
                          <div className="bar-label">{formatDate(entry.date)}</div>
                        </div>
                      )
                    })}
                  </div>
                </div>

                <div className="chart-legend">
                  <div className="legend-item">
                    <span className="legend-color legend-color--dashboard"></span>
                    <span className="legend-label">Dashboard (avg: {fuelData.summary.avg_dashboard_km_per_l.toFixed(1)} km/L)</span>
                  </div>
                  <div className="legend-item">
                    <span className="legend-color legend-color--calculated"></span>
                    <span className="legend-label">Calculated (avg: {fuelData.summary.avg_calculated_km_per_l.toFixed(1)} km/L)</span>
                  </div>
                </div>
              </>
            ) : (
              <div className="no-data">
                <p className="text-muted">No fuel economy data available</p>
                <p className="text-muted text-sm">Log full tank refuels with dashboard km/L to see data</p>
              </div>
            )}
          </div>

          {/* Heater vs Driving Fuel Chart */}
          <div className="card stats-card">
            <div className="card-header">
              <h3 className="card-title">Heater vs Driving Fuel</h3>
              <span className="badge badge--muted">Per refuel cycle</span>
            </div>

            {fuelData?.heater_usage && fuelData.heater_usage.length > 0 ? (
              <>
                <div className="chart-container">
                  <div className="bar-chart">
                    {fuelData.heater_usage.map((cycle, index) => (
                      <div key={index} className="bar-wrapper">
                        <div className="bar-container" title={`${cycle.total_fuel_liters.toFixed(1)}L total (${cycle.days_in_cycle} days)`}>
                          <div
                            className="bar bar--driving"
                            style={{ height: `${(cycle.driving_fuel_liters / maxFuel) * 100}%` }}
                          />
                          <div
                            className="bar bar--heater"
                            style={{ height: `${(cycle.heater_usage_liters / maxFuel) * 100}%` }}
                          />
                        </div>
                        <div className="bar-label">{formatDate(cycle.date)}</div>
                        {cycle.days_in_cycle > 0 && (
                          <div className="bar-days-indicator">{cycle.days_in_cycle}d</div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                <div className="chart-legend">
                  <div className="legend-item">
                    <span className="legend-color legend-color--driving"></span>
                    <span className="legend-label">Driving fuel (total: {fuelData.summary.total_driving_fuel.toFixed(0)}L)</span>
                  </div>
                  <div className="legend-item">
                    <span className="legend-color legend-color--heater"></span>
                    <span className="legend-label">Heater (avg: {fuelData.summary.avg_heater_usage_l_per_day.toFixed(2)}L/day)</span>
                  </div>
                </div>

                {/* Heater stats */}
                <div className="fuel-stats">
                  <div className="stat-item">
                    <span className="stat-value">{fuelData.summary.total_heater_usage.toFixed(0)}</span>
                    <span className="stat-label">Total heater (L)</span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-value">{fuelData.summary.avg_heater_usage_l_per_day.toFixed(2)}</span>
                    <span className="stat-label">Avg L/day</span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-value">{fuelData.summary.total_driving_fuel.toFixed(0)}</span>
                    <span className="stat-label">Total driving (L)</span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-value">
                      {fuelData.summary.total_heater_usage + fuelData.summary.total_driving_fuel > 0
                        ? ((fuelData.summary.total_heater_usage / (fuelData.summary.total_heater_usage + fuelData.summary.total_driving_fuel)) * 100).toFixed(0)
                        : 0}%
                    </span>
                    <span className="stat-label">Heater share</span>
                  </div>
                </div>
              </>
            ) : (
              <div className="no-data">
                <p className="text-muted">No heater usage data available</p>
                <p className="text-muted text-sm">Log at least 2 full tank refuels to see data</p>
              </div>
            )}
          </div>

          {/* Monthly Fuel Costs Chart */}
          <div className="card stats-card">
            <div className="card-header">
              <h3 className="card-title">Monthly Fuel Costs</h3>
              <span className="badge badge--muted">Last 12 months</span>
            </div>

            {fuelData?.monthly_fuel_costs && fuelData.monthly_fuel_costs.length > 0 ? (
              <>
                <div className="chart-container">
                  <div className="bar-chart">
                    {fuelData.monthly_fuel_costs.map((month, index) => (
                      <div key={index} className="bar-wrapper">
                        <div
                          className="bar-container"
                          title={`${month.total_cost.toFixed(0)} (${month.total_liters.toFixed(0)}L, ${month.refuel_count} refuels)`}
                        >
                          <div
                            className="bar bar--cost"
                            style={{ height: `${(month.total_cost / maxCost) * 100}%` }}
                          />
                        </div>
                        <div className="bar-label">{formatMonth(month.month)}</div>
                        <div className="bar-cost-value">{month.total_cost.toFixed(0)}</div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="chart-legend">
                  <div className="legend-item">
                    <span className="legend-color legend-color--cost"></span>
                    <span className="legend-label">Monthly fuel cost (total: {fuelData.summary.total_cost.toFixed(0)})</span>
                  </div>
                </div>
              </>
            ) : (
              <div className="no-data">
                <p className="text-muted">No monthly cost data available</p>
              </div>
            )}
          </div>

          {/* Fuel Summary */}
          <div className="card stats-card">
            <div className="card-header">
              <h3 className="card-title">Fuel Summary</h3>
            </div>

            <div className="fuel-stats">
              <div className="stat-item">
                <span className="stat-value">{fuelData?.summary.total_refuels ?? 0}</span>
                <span className="stat-label">Total refuels</span>
              </div>
              <div className="stat-item">
                <span className="stat-value">{fuelData?.summary.total_fuel_liters.toFixed(0) ?? 0}</span>
                <span className="stat-label">Total liters</span>
              </div>
              <div className="stat-item">
                <span className="stat-value">{fuelData?.summary.total_cost.toFixed(0) ?? 0}</span>
                <span className="stat-label">Total cost</span>
              </div>
              <div className="stat-item">
                <span className="stat-value">{fuelData?.summary.avg_dashboard_km_per_l.toFixed(1) ?? '0.0'}</span>
                <span className="stat-label">Avg km/L</span>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Battery Section */}
      {section === 'battery' && (
        <>
          {/* SOC Over Time Chart */}
          <div className="card stats-card">
            <div className="card-header">
              <h3 className="card-title">Battery SOC</h3>
              <span className="badge badge--muted">Last 7 days</span>
            </div>

            {batteryData?.soc_over_time && batteryData.soc_over_time.length > 0 ? (
              <>
                <div className="chart-container">
                  <div className="soc-chart">
                    {batteryData.soc_over_time.map((point, index) => {
                      const total = batteryData.soc_over_time.length
                      const leftPos = total > 1 ? (index / (total - 1)) * 90 : 0 // 90% to leave room for labels
                      return (
                        <div
                          key={index}
                          className="soc-point"
                          style={{ bottom: `${point.soc}%`, left: `${leftPos}%` }}
                          title={`${point.soc.toFixed(1)}% @ ${new Date(point.timestamp).toLocaleString()}`}
                        />
                      )
                    })}
                    {/* SOC level guidelines */}
                    <div className="soc-guideline soc-guideline--100" />
                    <div className="soc-guideline soc-guideline--75" />
                    <div className="soc-guideline soc-guideline--50" />
                    <div className="soc-guideline soc-guideline--25" />
                    <div className="soc-guideline soc-guideline--0" />
                    <div className="soc-label soc-label--100">100%</div>
                    <div className="soc-label soc-label--50">50%</div>
                    <div className="soc-label soc-label--0">0%</div>
                  </div>
                </div>

                <div className="chart-legend">
                  <div className="legend-item">
                    <span className="legend-color legend-color--soc"></span>
                    <span className="legend-label">State of Charge ({batteryData.summary.total_readings} readings)</span>
                  </div>
                </div>
              </>
            ) : (
              <div className="no-data">
                <p className="text-muted">No battery data available</p>
                <p className="text-muted text-sm">Battery readings will appear here once collected</p>
              </div>
            )}
          </div>

          {/* Daily Pattern Chart */}
          <div className="card stats-card">
            <div className="card-header">
              <h3 className="card-title">Daily Pattern</h3>
              <span className="badge badge--muted">Min/Avg/Max SOC</span>
            </div>

            {batteryData?.daily_pattern && batteryData.daily_pattern.some(d => d.readings > 0) ? (
              <>
                <div className="chart-container">
                  <div className="bar-chart-with-axis">
                    <div className="bar-chart-axis">
                      <span className="bar-chart-axis-label bar-chart-axis-label--100">100%</span>
                      <span className="bar-chart-axis-label bar-chart-axis-label--50">50%</span>
                      <span className="bar-chart-axis-label bar-chart-axis-label--0">0%</span>
                    </div>
                    <div className="bar-chart">
                      {batteryData.daily_pattern.map((day, index) => (
                        <div key={index} className="bar-wrapper">
                          <div
                            className="bar-container bar-container--battery"
                            title={`${formatDate(day.date)}: Avg ${day.avg_soc.toFixed(1)}%, Min ${day.min_soc.toFixed(1)}%, Max ${day.max_soc.toFixed(1)}%`}
                          >
                            {day.readings > 0 ? (
                              <>
                                {/* Range bar showing min to max */}
                                <div
                                  className="bar bar--battery-range"
                                  style={{
                                    height: `${day.max_soc - day.min_soc}%`,
                                    bottom: `${day.min_soc}%`
                                  }}
                                />
                                {/* Average marker */}
                                <div
                                  className="battery-avg-marker"
                                  style={{ bottom: `${day.avg_soc}%` }}
                                />
                              </>
                            ) : (
                              <div className="bar bar--empty" />
                            )}
                          </div>
                          <div className="bar-label">{formatDate(day.date)}</div>
                          {day.readings > 0 && (
                            <div className="bar-soc-value">{day.avg_soc.toFixed(0)}%</div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="chart-legend">
                  <div className="legend-item">
                    <span className="legend-color legend-color--battery-range"></span>
                    <span className="legend-label">SOC range (min-max)</span>
                  </div>
                  <div className="legend-item">
                    <span className="legend-color legend-color--battery-avg"></span>
                    <span className="legend-label">Average SOC</span>
                  </div>
                </div>
              </>
            ) : (
              <div className="no-data">
                <p className="text-muted">No daily pattern data available</p>
              </div>
            )}
          </div>

          {/* Battery Summary */}
          <div className="card stats-card">
            <div className="card-header">
              <h3 className="card-title">Battery Summary</h3>
            </div>

            <div className="battery-stats">
              <div className="stat-item">
                <span className="stat-value">{batteryData?.summary.current_soc.toFixed(2) ?? '--'}%</span>
                <span className="stat-label">Current SOC</span>
              </div>
              <div className="stat-item">
                <span className="stat-value">{batteryData?.summary.current_voltage.toFixed(3) ?? '--'}V</span>
                <span className="stat-label">Current Voltage</span>
              </div>
              <div className="stat-item">
                <span className="stat-value">{batteryData?.summary.avg_daily_consumption.toFixed(1) ?? '0'}%</span>
                <span className="stat-label">Avg Daily Use</span>
              </div>
              <div className="stat-item">
                <span className="stat-value">{batteryData?.summary.days_with_data ?? 0}</span>
                <span className="stat-label">Days tracked</span>
              </div>
            </div>

            <div className="battery-range-summary">
              <div className="range-item">
                <span className="range-label">7-day Average:</span>
                <span className="range-value">{batteryData?.summary.avg_soc.toFixed(1) ?? '--'}%</span>
              </div>
              <div className="range-item">
                <span className="range-label">7-day Minimum:</span>
                <span className="range-value">{batteryData?.summary.min_soc.toFixed(1) ?? '--'}%</span>
              </div>
              <div className="range-item">
                <span className="range-label">7-day Maximum:</span>
                <span className="range-value">{batteryData?.summary.max_soc.toFixed(1) ?? '--'}%</span>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Expenses Section */}
      {section === 'expenses' && (
        <>
          {/* Expenses by Category */}
          <div className="card stats-card">
            <div className="card-header">
              <h3 className="card-title">Spending by Category</h3>
              <span className="badge badge--muted">{expensesData?.native_currency ?? 'DKK'}</span>
            </div>

            {expensesData?.by_category && expensesData.by_category.length > 0 ? (
              <>
                <div className="chart-container">
                  <div className="bar-chart bar-chart--horizontal">
                    {expensesData.by_category.map((cat, index) => {
                      const maxValue = Math.max(...expensesData.by_category.map(c => c.total_native), 1)
                      return (
                        <div key={index} className="horizontal-bar-wrapper">
                          <div className="horizontal-bar-label">{cat.category}</div>
                          <div className="horizontal-bar-container">
                            <div
                              className="horizontal-bar"
                              style={{ width: `${(cat.total_native / maxValue) * 100}%` }}
                              title={`${cat.total_native.toFixed(0)} ${expensesData.native_currency} (${cat.count} expenses)`}
                            />
                          </div>
                          <div className="horizontal-bar-value">{cat.total_native.toFixed(0)}</div>
                        </div>
                      )
                    })}
                  </div>
                </div>

                <div className="chart-legend">
                  <div className="legend-item">
                    <span className="legend-color legend-color--expenses"></span>
                    <span className="legend-label">Total: {expensesData.summary.total_spend_native.toFixed(0)} {expensesData.native_currency}</span>
                  </div>
                </div>
              </>
            ) : (
              <div className="no-data">
                <p className="text-muted">No expense data available</p>
                <p className="text-muted text-sm">Log expenses, fuel, or propane purchases to see data</p>
              </div>
            )}
          </div>

          {/* Monthly Expense Breakdown */}
          <div className="card stats-card">
            <div className="card-header">
              <h3 className="card-title">Monthly Expenses</h3>
              <span className="badge badge--muted">Last 12 months</span>
            </div>

            {expensesData?.monthly_breakdown && expensesData.monthly_breakdown.length > 0 ? (
              <>
                <div className="chart-container">
                  <div className="bar-chart">
                    {expensesData.monthly_breakdown.map((month, index) => {
                      const maxValue = Math.max(...expensesData.monthly_breakdown.map(m => m.total_native), 1)
                      return (
                        <div key={index} className="bar-wrapper">
                          <div
                            className="bar-container"
                            title={`${month.total_native.toFixed(0)} ${expensesData.native_currency} (${month.count} expenses)`}
                          >
                            <div
                              className="bar bar--expenses"
                              style={{ height: `${(month.total_native / maxValue) * 100}%` }}
                            />
                          </div>
                          <div className="bar-label">{formatMonth(month.month)}</div>
                          <div className="bar-expense-value">{month.total_native.toFixed(0)}</div>
                        </div>
                      )
                    })}
                  </div>
                </div>

                <div className="chart-legend">
                  <div className="legend-item">
                    <span className="legend-color legend-color--expenses"></span>
                    <span className="legend-label">Monthly spending (avg: {expensesData.summary.avg_per_month.toFixed(0)} {expensesData.native_currency}/month)</span>
                  </div>
                </div>
              </>
            ) : (
              <div className="no-data">
                <p className="text-muted">No monthly expense data available</p>
              </div>
            )}
          </div>

          {/* Expenses Summary */}
          <div className="card stats-card">
            <div className="card-header">
              <h3 className="card-title">Expenses Summary</h3>
            </div>

            <div className="expenses-stats">
              <div className="stat-item">
                <span className="stat-value">{expensesData?.summary.total_expenses ?? 0}</span>
                <span className="stat-label">Total expenses</span>
              </div>
              <div className="stat-item">
                <span className="stat-value">{expensesData?.summary.total_spend_native.toFixed(0) ?? 0}</span>
                <span className="stat-label">Total {expensesData?.native_currency ?? 'DKK'}</span>
              </div>
              <div className="stat-item">
                <span className="stat-value">{expensesData?.summary.avg_per_month.toFixed(0) ?? 0}</span>
                <span className="stat-label">Avg/month</span>
              </div>
              <div className="stat-item">
                <span className="stat-value">{expensesData?.summary.top_category || '--'}</span>
                <span className="stat-label">Top category</span>
              </div>
            </div>

            {expensesData?.summary.top_category && (
              <div className="expenses-top-category">
                <span className="top-category-label">Highest spending category:</span>
                <span className="top-category-value">{expensesData.summary.top_category} ({expensesData.summary.top_category_spend.toFixed(0)} {expensesData.native_currency})</span>
              </div>
            )}
          </div>
        </>
      )}

      {/* Propane Section */}
      {section === 'propane' && (
        <>
          {/* Days Between Replacements Chart */}
          <div className="card stats-card">
            <div className="card-header">
              <h3 className="card-title">Days Between Replacements</h3>
              <span className="badge badge--muted">Tank usage</span>
            </div>

            {propaneData?.days_between && propaneData.days_between.length > 0 ? (
              <>
                <div className="chart-container">
                  <div className="bar-chart">
                    {propaneData.days_between.map((entry, index) => {
                      const maxDays = Math.max(...propaneData.days_between.map(e => e.days_between), 1)
                      return (
                        <div key={index} className="bar-wrapper">
                          <div
                            className="bar-container"
                            title={`${entry.days_between} days (Tank #${entry.replacement_num})`}
                          >
                            <div
                              className="bar bar--propane"
                              style={{ height: `${(entry.days_between / maxDays) * 100}%` }}
                            />
                          </div>
                          <div className="bar-label">{formatDate(entry.date)}</div>
                          <div className="bar-days-value">{entry.days_between}d</div>
                        </div>
                      )
                    })}
                  </div>
                </div>

                <div className="chart-legend">
                  <div className="legend-item">
                    <span className="legend-color legend-color--propane"></span>
                    <span className="legend-label">Days between tank replacements (avg: {propaneData.summary.avg_days_per_tank.toFixed(0)} days)</span>
                  </div>
                </div>
              </>
            ) : (
              <div className="no-data">
                <p className="text-muted">No propane history data available</p>
                <p className="text-muted text-sm">Log at least 2 propane replacements to see usage data</p>
              </div>
            )}
          </div>

          {/* Propane Summary */}
          <div className="card stats-card">
            <div className="card-header">
              <h3 className="card-title">Propane Summary</h3>
            </div>

            <div className="propane-stats">
              <div className="stat-item">
                <span className="stat-value">{propaneData?.summary.total_replacements ?? 0}</span>
                <span className="stat-label">Total tanks</span>
              </div>
              <div className="stat-item">
                <span className="stat-value">{propaneData?.summary.avg_days_per_tank.toFixed(0) ?? '--'}</span>
                <span className="stat-label">Avg days/tank</span>
              </div>
              <div className="stat-item">
                <span className="stat-value">{propaneData?.summary.days_since_last_replace ?? '--'}</span>
                <span className="stat-label">Current days</span>
              </div>
              <div className="stat-item">
                <span className="stat-value">{propaneData?.summary.total_cost.toFixed(0) ?? 0}</span>
                <span className="stat-label">Total {propaneData?.native_currency ?? 'DKK'}</span>
              </div>
            </div>

            {propaneData?.summary.avg_days_per_tank && propaneData.summary.avg_days_per_tank > 0 && (
              <div className="propane-range-summary">
                <div className="range-item">
                  <span className="range-label">Shortest tank duration:</span>
                  <span className="range-value">{propaneData.summary.min_days} days</span>
                </div>
                <div className="range-item">
                  <span className="range-label">Longest tank duration:</span>
                  <span className="range-value">{propaneData.summary.max_days} days</span>
                </div>
                <div className="range-item">
                  <span className="range-label">Annual cost estimate:</span>
                  <span className="range-value">{propaneData.summary.annual_cost_estimate.toFixed(0)} {propaneData.native_currency}</span>
                </div>
              </div>
            )}
          </div>

          {/* Replacement History */}
          <div className="card stats-card">
            <div className="card-header">
              <h3 className="card-title">Replacement History</h3>
              <span className="badge badge--muted">{propaneData?.replacement_history.length ?? 0} replacements</span>
            </div>

            {propaneData?.replacement_history && propaneData.replacement_history.length > 0 ? (
              <div className="propane-list">
                {propaneData.replacement_history.map((replacement) => (
                  <div key={replacement.id} className="propane-item">
                    <div className="propane-info">
                      <div className="propane-date">{formatTimestamp(replacement.timestamp)}</div>
                      {replacement.tank_size_kg && (
                        <div className="propane-size">{replacement.tank_size_kg} kg tank</div>
                      )}
                      {replacement.notes && (
                        <div className="propane-notes">{replacement.notes}</div>
                      )}
                    </div>
                    <div className="propane-details">
                      {replacement.days_since_prev !== undefined && replacement.days_since_prev > 0 && (
                        <div className="propane-days">{replacement.days_since_prev}d</div>
                      )}
                      {replacement.cost !== undefined && replacement.cost > 0 && (
                        <div className="propane-cost">{replacement.cost.toFixed(0)} {replacement.currency || propaneData.native_currency}</div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="no-data">
                <p className="text-muted">No replacement history available</p>
              </div>
            )}
          </div>
        </>
      )}

      {/* AdBlue Section */}
      {section === 'adblue' && (
        <>
          {/* Consumption Rate Chart */}
          <div className="card stats-card">
            <div className="card-header">
              <h3 className="card-title">Consumption Rate</h3>
              <span className="badge badge--muted">km/L</span>
            </div>

            {adblueData?.consumption_data && adblueData.consumption_data.length > 0 ? (
              <>
                <div className="chart-container">
                  <div className="bar-chart">
                    {adblueData.consumption_data.map((entry, index) => {
                      const maxKmL = Math.max(...adblueData.consumption_data.map(e => e.km_per_liter), 1)
                      return (
                        <div key={index} className="bar-wrapper">
                          <div
                            className="bar-container"
                            title={`${entry.km_per_liter.toFixed(0)} km/L (${entry.distance_km.toFixed(0)} km / ${entry.amount_liters.toFixed(1)}L)`}
                          >
                            <div
                              className="bar bar--adblue"
                              style={{ height: `${(entry.km_per_liter / maxKmL) * 100}%` }}
                            />
                          </div>
                          <div className="bar-label">{formatDate(entry.date)}</div>
                          <div className="bar-kml-value">{entry.km_per_liter.toFixed(0)}</div>
                        </div>
                      )
                    })}
                  </div>
                </div>

                <div className="chart-legend">
                  <div className="legend-item">
                    <span className="legend-color legend-color--adblue"></span>
                    <span className="legend-label">Consumption rate (avg: {adblueData.summary.avg_km_per_liter.toFixed(0)} km/L)</span>
                  </div>
                </div>
              </>
            ) : (
              <div className="no-data">
                <p className="text-muted">No consumption data available</p>
                <p className="text-muted text-sm">Log at least 2 AdBlue refills with odometer readings to see data</p>
              </div>
            )}
          </div>

          {/* AdBlue Summary */}
          <div className="card stats-card">
            <div className="card-header">
              <h3 className="card-title">AdBlue Summary</h3>
            </div>

            <div className="adblue-stats">
              <div className="stat-item">
                <span className="stat-value">{adblueData?.summary.total_refills ?? 0}</span>
                <span className="stat-label">Total refills</span>
              </div>
              <div className="stat-item">
                <span className="stat-value">{adblueData?.summary.total_liters.toFixed(1) ?? '0'}</span>
                <span className="stat-label">Total liters</span>
              </div>
              <div className="stat-item">
                <span className="stat-value">{adblueData?.summary.avg_km_per_liter.toFixed(0) ?? '--'}</span>
                <span className="stat-label">Avg km/L</span>
              </div>
              <div className="stat-item">
                <span className="stat-value">{adblueData?.summary.total_cost.toFixed(0) ?? 0}</span>
                <span className="stat-label">Total {adblueData?.native_currency ?? 'DKK'}</span>
              </div>
            </div>

            {adblueData?.summary.avg_liters_per_month && adblueData.summary.avg_liters_per_month > 0 && (
              <div className="adblue-range-summary">
                <div className="range-item">
                  <span className="range-label">Average monthly usage:</span>
                  <span className="range-value">{adblueData.summary.avg_liters_per_month.toFixed(1)} L/month</span>
                </div>
                {adblueData.summary.annual_cost_estimate > 0 && (
                  <div className="range-item">
                    <span className="range-label">Annual cost estimate:</span>
                    <span className="range-value">{adblueData.summary.annual_cost_estimate.toFixed(0)} {adblueData.native_currency}</span>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Refill History */}
          <div className="card stats-card">
            <div className="card-header">
              <h3 className="card-title">Refill History</h3>
              <span className="badge badge--muted">{adblueData?.refill_history.length ?? 0} refills</span>
            </div>

            {adblueData?.refill_history && adblueData.refill_history.length > 0 ? (
              <div className="adblue-list">
                {adblueData.refill_history.map((refill) => (
                  <div key={refill.id} className="adblue-item">
                    <div className="adblue-info">
                      <div className="adblue-date">{formatTimestamp(refill.timestamp)}</div>
                      {refill.odometer && (
                        <div className="adblue-odometer">{refill.odometer.toFixed(0)} km</div>
                      )}
                      {refill.notes && (
                        <div className="adblue-notes">{refill.notes}</div>
                      )}
                    </div>
                    <div className="adblue-details">
                      <div className="adblue-amount">{refill.amount_liters.toFixed(1)} L</div>
                      {refill.km_per_liter && (
                        <div className="adblue-kml">{refill.km_per_liter.toFixed(0)} km/L</div>
                      )}
                      {refill.price !== undefined && refill.price > 0 && (
                        <div className="adblue-cost">{refill.price.toFixed(0)} {refill.currency || adblueData.native_currency}</div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="no-data">
                <p className="text-muted">No refill history available</p>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  )
}
